import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, of, Subject, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { SpacesDispensesHD } from 'src/app/shared/interfaces/IHDspace';
import { ApiErrorHandlerService } from 'src/app/shared/services/api-error-handler/api-error-handler.service';
import { HelmsdeepService } from 'src/app/shared/services/helmsdeep/helmsdeep.service';
import { RearLoadingStore } from 'src/app/shared/services/store/rear-loading/rear-loading.store';
import { FRICTIONLESS_FLOW, SPACE_STATES, TOAST_TYPES } from 'src/app/shared/utils/constants';
import { MAX_USE_ADD_COMPARTMENT } from 'src/environments/runtime';

type SessionSpaceValidation = {
  faultyPositions: string[];
  goodPositions: string[];
};

@Injectable({ providedIn: 'root' })
export class RearLoadingFrictionlessGuard {
  private readonly maxAddCompartment = Number(MAX_USE_ADD_COMPARTMENT);
  private readonly messageCompartmentAllowed = 'common.messageCompartmentAllowed';
  private readonly faultySpaceDeliveryMessage = 'common.faultySpaceDeliveryMessage';
  private readonly faultySpaceDeliveryBlockedMessage = 'common.faultySpaceDeliveryBlockedMessage';
  private readonly noGoodSpacesRetryDelayMs = 500;
  private readonly positionsToValidate$ = new Subject<string[]>();
  private readonly announcedFaultyPositions = new Set<string>();
  maxCompartmentsExceeded = false;
  faultySpacesBlocked = false;

  get disableEnter(): boolean {
    return this.maxCompartmentsExceeded;
  }

  readonly faultySpacesBlockedChanges$ = this.positionsToValidate$.pipe(
    switchMap((positions) => this.updateFaultySpacesBlocked(positions))
  );

  constructor(
    private readonly apiErrorHandlerService: ApiErrorHandlerService,
    private readonly helmsdeepService: HelmsdeepService,
    private readonly translateService: TranslateService,
    private readonly rearLoadingStore: RearLoadingStore
  ) {}

  reset(): void {
    this.maxCompartmentsExceeded = false;
    this.faultySpacesBlocked = false;
    this.announcedFaultyPositions.clear();
  }

  isFrictionlessModeOn(validatePresenceSensor: boolean, flow: string): boolean {
    return validatePresenceSensor && flow === FRICTIONLESS_FLOW;
  }

  getPositionsFromRoute(activatedRoute: ActivatedRoute): string[] {
    const positions = activatedRoute.snapshot.paramMap.get('positions');
    if (!positions) {
      return this.rearLoadingStore.getSessionPositions();
    }

    return JSON.parse(positions);
  }

  updatePositionsForPresence(positions: string[], position: string, presence: boolean): string[] {
    const nextPositions = [...positions];
    const positionIndex = nextPositions.findIndex(
      (currentPosition) => currentPosition === position
    );

    if (presence && positionIndex === -1) {
      nextPositions.push(position);
    } else if (!presence && positionIndex !== -1) {
      nextPositions.splice(positionIndex, 1);
    }

    if (!presence) {
      this.clearFaultyTrackingForUnfilledPosition(position, nextPositions);
    }

    return nextPositions;
  }

  updateMaxCompartmentsExceeded(positions: string[]): void {
    this.rearLoadingStore.updateSessionPositions(positions);
    const exceeded = positions.length > this.maxAddCompartment;
    if (exceeded && !this.maxCompartmentsExceeded) {
      this.showMaxCompartmentsAllowedMessage();
    }
    this.maxCompartmentsExceeded = exceeded;
  }

  updateSessionGuards(positions: string[]): void {
    this.updateMaxCompartmentsExceeded(positions);
    this.positionsToValidate$.next(positions);
  }

  private updateFaultySpacesBlocked(positions: string[]): Observable<boolean> {
    if (!positions.length) {
      this.faultySpacesBlocked = false;
      this.announcedFaultyPositions.clear();
      return of(false);
    }

    return this.getFilledSpacesValidation(positions).pipe(
      switchMap((validation) => {
        if (validation.goodPositions.length) {
          return of(validation);
        }

        return timer(this.noGoodSpacesRetryDelayMs).pipe(
          switchMap(() => this.getFilledSpacesValidation(positions))
        );
      }),
      tap(({ faultyPositions }) => {
        const newFaultyPositions = faultyPositions.filter(
          (position) => !this.announcedFaultyPositions.has(position)
        );
        const blocked = faultyPositions.length > 0;

        if (newFaultyPositions.length) {
          this.showFaultySpacesMessage(newFaultyPositions);
        }

        this.syncAnnouncedFaultyPositions(faultyPositions);
        this.faultySpacesBlocked = blocked;
      }),
      map(({ faultyPositions }) => faultyPositions.length > 0),
      catchError((error) => {
        this.apiErrorHandlerService.handleApiError(error, '');
        this.faultySpacesBlocked = true;
        return of(true);
      })
    );
  }

  preventDeliveryWhenFaultySpaces(): boolean {
    if (!this.faultySpacesBlocked) {
      return false;
    }

    this.showFaultySpacesBlockedMessage();
    return true;
  }

  validateCanStartDelivery(positions: string[]): Observable<boolean> {
    if (!positions.length) {
      this.faultySpacesBlocked = false;
      return of(true);
    }

    return this.getFilledSpacesValidation(positions).pipe(
      map(({ faultyPositions }) => {
        const blocked = faultyPositions.length > 0;
        this.faultySpacesBlocked = blocked;

        if (blocked) {
          this.showFaultySpacesBlockedMessage();
        }

        return !blocked;
      }),
      catchError((error) => {
        this.apiErrorHandlerService.handleApiError(error, '');
        this.faultySpacesBlocked = true;
        return of(false);
      })
    );
  }

  private getSessionSpaceValidation(
    spaces: SpacesDispensesHD[],
    positions: string[]
  ): SessionSpaceValidation {
    const sessionPositions = new Set(positions);
    const sessionSpaces = spaces.filter((space) =>
      sessionPositions.has(space.hardwareInfo?.position)
    );

    return {
      faultyPositions: sessionSpaces
        .filter((space) => this.isFilledFaultySpace(space))
        .map((space) => space.hardwareInfo.position),
      goodPositions: sessionSpaces
        .filter((space) => this.isFilledGoodSpace(space))
        .map((space) => space.hardwareInfo.position),
    };
  }

  private getFilledSpacesValidation(positions: string[]): Observable<SessionSpaceValidation> {
    return this.helmsdeepService
      .getSpaces({ presence: SPACE_STATES.FILL })
      .pipe(
        map((spaces: SpacesDispensesHD[]) => this.getSessionSpaceValidation(spaces, positions))
      );
  }

  private syncAnnouncedFaultyPositions(faultyPositions: string[]): void {
    this.announcedFaultyPositions.clear();
    faultyPositions.forEach((position) => this.announcedFaultyPositions.add(position));
  }

  private clearFaultyTrackingForUnfilledPosition(position: string, nextPositions: string[]): void {
    this.announcedFaultyPositions.delete(position);

    if (!nextPositions.length) {
      this.faultySpacesBlocked = false;
      this.maxCompartmentsExceeded = false;
    }
  }

  private showMaxCompartmentsAllowedMessage(): void {
    this.apiErrorHandlerService.showMessage(
      this.translateService.instant(this.messageCompartmentAllowed, {
        maxCompartments: this.maxAddCompartment,
      }),
      TOAST_TYPES.Info
    );
  }

  private isFilledFaultySpace(space: SpacesDispensesHD): boolean {
    const spaceState = space?.state;

    return spaceState?.presence === SPACE_STATES.FILL && spaceState?.secure === SPACE_STATES.OPEN;
  }

  private isFilledGoodSpace(space: SpacesDispensesHD): boolean {
    const spaceState = space?.state;

    return (
      spaceState?.presence === SPACE_STATES.FILL &&
      spaceState?.selectable !== SPACE_STATES.DISABLE &&
      spaceState?.secure === SPACE_STATES.CLOSE
    );
  }

  private showFaultySpacesMessage(positions: string[]): void {
    positions.forEach((position) => {
      this.apiErrorHandlerService.showMessage(
        this.translateService.instant(this.faultySpaceDeliveryMessage, {
          position,
        }),
        TOAST_TYPES.Warning
      );
    });
  }

  private showFaultySpacesBlockedMessage(): void {
    this.apiErrorHandlerService.showMessage(
      this.translateService.instant(this.faultySpaceDeliveryBlockedMessage),
      TOAST_TYPES.Error
    );
  }
}
